import { ChangeEvent, FC, useEffect, useMemo, useState } from "react";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";

import { MenuItem, Stack, Switch, Typography } from "@mui/material";
import MakeSelect from "../../../common/components/layout/components/MakeSelect";
import useLocationFromCache from "../../../common/hooks/useLocationFromCache";
import { skuCatalog, skuMap } from "../../../common/models/enums";
import {
  isLegacySKU,
  isSKUAssigned,
} from "../../../common/variables/skuOptions";
import {
  panTiltTypeOptions,
  SKU_MAKE_MODEL_TO_TYPE,
} from "../../system-settings/tabs/skus/variables/skuData";
import {
  defaultDeviceFormValidationState,
  deviceFormRules,
  deviceFormValidationStateVariable,
  deviceFormVariables,
  useDeviceFormValidationState,
  useDeviceFormVariables,
} from "../variables/devices";
import DeviceDynamicRadioButtonInput from "./DynamicInputs/DeviceDynamicRadioButtonInput";
import DeviceDynamicTextInput from "./DynamicInputs/DeviceDynamicTextInput";

interface IDeviceDetailsFormProps {
  disablePtToggle?: boolean;
}

const DeviceDetailsForm: FC<IDeviceDetailsFormProps> = ({
  disablePtToggle,
}) => {
  const deviceForm = useDeviceFormVariables();
  const validation = useDeviceFormValidationState();
  const { getCachedLocation } = useLocationFromCache();
  const location = getCachedLocation(deviceForm.location?.value ?? "");
  const [forceTCP, setForceTCP] = useState(
    deviceForm.deviceData.protocol === "tcp"
  );

  const [hasRecordVideo, setHasRecordVideo] = useState(
    deviceForm.deviceData.hasRecordVideo
  );

  const setDeviceName = (event: ChangeEvent<HTMLInputElement>): void => {
    deviceFormValidationStateVariable({
      ...validation,
      deviceName: { ...defaultDeviceFormValidationState.deviceName },
    });

    const deviceName = event.target.value;

    deviceFormVariables({
      ...deviceForm,
      isDirty: true,
      deviceName,
    });

    let hasError = !deviceName.match(deviceFormRules.deviceName.pattern);
    let errorMessage = "";

    if (hasError) {
      errorMessage = "Valid characters: A-Z, a-z, 0-9, -";
    }

    // NOTE: This rule is handled on submit
    if (hasError && !deviceName) {
      hasError = false;

      errorMessage = "";
    }

    deviceFormValidationStateVariable({
      ...validation,
      deviceName: {
        hasError,
        errorMessage,
      },
    });
  };

  const setDeviceType = (value: string): void => {
    deviceFormVariables({
      ...deviceForm,
      isDirty: true,
      deviceType: value,
    });
  };

  const setDeviceConfig = (
    name: string,
    value: string | boolean | null
  ): void => {
    deviceFormVariables({
      ...deviceForm,
      isDirty: true,
      deviceData: {
        ...deviceForm.deviceData,
        [name]: value,
      },
    });

    let validationState = { ...validation };

    if (name === "panTiltIP") {
      if (deviceForm.deviceData.hasPanTilt && value) {
        validationState = {
          ...validationState,
          panTiltIP: {
            hasError: false,
            errorMessage: "",
          },
        };
      }
    }

    deviceFormValidationStateVariable(validationState);
  };

  const setCameraIpAddress = (event: ChangeEvent<HTMLInputElement>): void => {
    const cameraIpAddress = event.target.value.replaceAll(" ", "");

    const rtspTemplate = deviceForm.rtspTemplate;
    let sourceVideoValue = deviceForm.deviceData?.sourceVideo;

    if (deviceForm.defaultUserPwd) {
      sourceVideoValue = `rtsp://${deviceForm.defaultUserPwd}@${cameraIpAddress}${rtspTemplate}`;
    } else {
      sourceVideoValue = `rtsp://${cameraIpAddress}${rtspTemplate}`;
    }

    deviceFormVariables({
      ...deviceForm,
      isDirty: true,
      cameraIpAddress,
      deviceData: {
        ...deviceForm.deviceData,
        sourceVideo: sourceVideoValue,
      },
    });

    deviceFormValidationStateVariable({
      ...validation,
      cameraIpAddress: {
        hasError: false,
        errorMessage: "",
      },
    });
  };

  const handleForceTcpChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;

    setForceTCP(isChecked);

    setDeviceConfig("protocol", isChecked ? "tcp" : "udp");
  };

  const handleEnableRecordingChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isChecked = event.target.checked;

    setHasRecordVideo(isChecked);

    setDeviceConfig("hasRecordVideo", isChecked);
  };

  const handleSKUNumberChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newSkuNumber = event.target.value;

    deviceFormVariables({
      ...deviceForm,
      isDirty: true,
      skuNumber: newSkuNumber || null,
      configurationName: skuMap[newSkuNumber as keyof typeof skuMap] ?? null,
    });
  };

  const handleSerialNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    deviceFormVariables({
      ...deviceForm,
      isDirty: true,
      serialNumber: event.target.value || null,
    });
  };

  const handleComponentSerialNumberChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    deviceFormVariables({
      ...deviceForm,
      isDirty: true,
      componentSerialNumber: event.target.value || null,
    });
  };

  const deviceDataInput = useMemo(() => {
    return { ...deviceForm.deviceData };
  }, [deviceForm.deviceData]);

  const showPanTilt = useMemo(() => {
    if (deviceForm.skuNumber && skuCatalog[deviceForm.skuNumber]) {
      // use sku catalog as single source of truth for PT toggle and view ability
      const component = skuCatalog[deviceForm.skuNumber].subComponents.find(
        sub => sub.makeModelId === deviceForm.deviceType
      );

      if (component) {
        return component.hasPT;
      }
    }

    // return fallback value for legacy devices
    return deviceForm.deviceMakePanTiltValue;
  }, [
    deviceForm.deviceMakePanTiltValue,
    deviceForm.skuNumber,
    deviceForm.deviceType,
  ]);

  const showPanTiltIp = useMemo(
    () => deviceDataInput.hasPanTilt,
    [deviceDataInput.hasPanTilt]
  );

  // PT Type is disabled (immutable) for SKU-onboarded devices that already have a PT Type set
  // Legacy devices: no skuNumber or skuNumber === "000-000"
  const isPtTypeDisabled = useMemo(() => {
    const isLegacyDevice =
      !deviceForm.skuNumber || deviceForm.skuNumber === "000-000";
    const hasInitialPtType = !!deviceForm.initialPanTiltType;

    return !isLegacyDevice && hasInitialPtType;
  }, [deviceForm.skuNumber, deviceForm.initialPanTiltType]);

  useEffect(() => {
    setForceTCP(deviceForm.deviceData.protocol === "tcp");
  }, [deviceForm.deviceData.protocol]);

  useEffect(() => {
    setHasRecordVideo(deviceForm.deviceData.hasRecordVideo);
  }, [deviceForm.deviceData.hasRecordVideo]);

  return (
    <>
      <Grid
        container
        rowSpacing={1}
        columnSpacing={{ xs: 1, sm: 2, md: 3 }}
        sx={{
          "& .MuiFormControl-root": {
            marginTop: 0,
            marginBottom: "1em",
          },
        }}
      >
        <Grid item xs={6}>
          <TextField
            margin="dense"
            fullWidth
            disabled
            label="Device ID"
            value={deviceForm.id.replace("DE#", "")}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            margin="dense"
            error={validation?.deviceName.hasError && !deviceForm.deviceName}
            fullWidth
            label="Device Name"
            value={deviceForm.deviceName}
            onChange={setDeviceName}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            margin="dense"
            fullWidth
            disabled
            label="Location"
            value={location?.name ?? ""}
          />
        </Grid>
        <Grid item xs={6}>
          {deviceForm.deviceType &&
          SKU_MAKE_MODEL_TO_TYPE[deviceForm.deviceType] ? (
            <TextField
              margin="dense"
              fullWidth
              disabled
              label="Device model"
              value={deviceForm.deviceType?.replace(/_/g, " ") ?? ""}
            />
          ) : (
            <MakeSelect
              disabled
              label="Device model"
              hasError={validation.deviceType.hasError}
              makeId={deviceForm.deviceType}
              onMakeChange={setDeviceType}
            />
          )}
        </Grid>

        <Grid item xs={6}>
          <TextField
            margin="dense"
            fullWidth
            disabled
            label="Node"
            value={deviceForm.node?.name ?? ""}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            margin="dense"
            error={validation?.cameraIpAddress.hasError}
            helperText={validation?.cameraIpAddress.errorMessage}
            fullWidth
            label="Camera IP Address"
            value={deviceForm.cameraIpAddress ?? ""}
            onChange={setCameraIpAddress}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            select
            margin="dense"
            fullWidth
            label="SKU Number"
            value={deviceForm.skuNumber ?? ""}
            onChange={handleSKUNumberChange}
            helperText={
              !isSKUAssigned(deviceForm.skuNumber)
                ? "No SKU assigned"
                : isLegacySKU(deviceForm.skuNumber)
                ? "Device predates SKU tracking"
                : "SKU identifier"
            }
          >
            <MenuItem value="">
              <em>No SKU selected</em>
            </MenuItem>
            {Object.keys(skuMap).map(key => (
              <MenuItem key={key} value={key}>
                {key === "000-000" ? "000-000 (Legacy)" : key}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6}>
          <TextField
            margin="dense"
            fullWidth
            label="Product Name"
            value={
              isLegacySKU(deviceForm.skuNumber)
                ? "Legacy"
                : skuMap[deviceForm.skuNumber as keyof typeof skuMap] ?? ""
            }
            disabled
            helperText={
              !isSKUAssigned(deviceForm.skuNumber)
                ? "No SKU assigned"
                : "Derived from SKU"
            }
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            margin="dense"
            fullWidth
            label="Serial Number"
            value={deviceForm.serialNumber ?? ""}
            onChange={handleSerialNumberChange}
            helperText="Product serial number (USN)"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            margin="dense"
            fullWidth
            label="Component Serial Number"
            value={deviceForm.componentSerialNumber ?? ""}
            onChange={handleComponentSerialNumberChange}
            helperText="Component serial number (CSN)"
          />
        </Grid>
        <Grid item>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography>Force TCP</Typography>
            <Switch checked={forceTCP} onChange={handleForceTcpChange} />
          </Stack>
        </Grid>
        <Grid item>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography>Enable Recording</Typography>
            <Switch
              checked={hasRecordVideo}
              onChange={handleEnableRecordingChange}
            />
          </Stack>
        </Grid>
      </Grid>
      <br />
      <Divider />
      <br />
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        <Grid item xs={6}>
          <DeviceDynamicTextInput
            key={"sourceVideo"}
            hasError={validation.sourceVideo.hasError}
            helperText={validation.sourceVideo.errorMessage}
            field={"sourceVideo"}
            label={"RTSP Source (Optional)"}
            deviceConfig={deviceForm.deviceData}
            setDeviceConfig={setDeviceConfig}
          />
        </Grid>
        <Grid item xs={6}>
          <DeviceDynamicTextInput
            key={"rtspHost"}
            hasError={false}
            field={"rtspHost"}
            label={"RTSP Client (Optional)"}
            deviceConfig={deviceForm.deviceData}
            setDeviceConfig={setDeviceConfig}
            disableInput
          />
        </Grid>
        {showPanTilt ? (
          <Grid item xs={6}>
            <DeviceDynamicRadioButtonInput
              key={"hasPanTilt"}
              field={"hasPanTilt"}
              label={"Enable Pan Tilt (Optional)"}
              entries={[
                { value: true, display: "Yes" },
                {
                  value: false,
                  display: (
                    <Tooltip
                      TransitionComponent={Zoom}
                      title={
                        disablePtToggle
                          ? "Device has assigned zones. Please remove them first"
                          : null
                      }
                    >
                      <Box
                        component="span"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5em",
                        }}
                      >
                        <span>No</span>
                        {disablePtToggle && (
                          <HelpOutlineIcon fontSize="small" />
                        )}
                      </Box>
                    </Tooltip>
                  ),
                },
              ]}
              deviceConfig={deviceForm.deviceData}
              setDeviceConfig={setDeviceConfig}
              disabled={disablePtToggle}
            />
          </Grid>
        ) : (
          ""
        )}
        {showPanTiltIp ? (
          <>
            <Grid item xs={6}>
              <DeviceDynamicTextInput
                key={"panTiltIP"}
                hasError={validation.panTiltIP.hasError}
                helperText={validation.panTiltIP.errorMessage}
                field={"panTiltIP"}
                label={"Pan Tilt IP"}
                deviceConfig={deviceForm.deviceData}
                setDeviceConfig={setDeviceConfig}
              />
            </Grid>
            <Grid item xs={6}>
              {isPtTypeDisabled ? (
                <TextField
                  fullWidth
                  label="PT Type"
                  value={deviceForm.deviceData.panTiltType || ""}
                  disabled
                  helperText="Pan-Tilt device type"
                  InputProps={{ readOnly: true }}
                />
              ) : (
                <TextField
                  select
                  fullWidth
                  label="PT Type"
                  value={deviceForm.deviceData.panTiltType || ""}
                  onChange={e => setDeviceConfig("panTiltType", e.target.value)}
                  helperText="Pan-Tilt device type"
                >
                  <MenuItem value="">
                    <em>Select PT Type</em>
                  </MenuItem>
                  {panTiltTypeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Grid>
          </>
        ) : (
          ""
        )}
      </Grid>
    </>
  );
};

export default DeviceDetailsForm;
